import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EntryStoreState } from './entry.model';
import { EntryActions } from './entry.actions';
import {
  catchError,
  EMPTY,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { CommonService } from '../common.service';
import { EntrySelectors } from './entry.selectors';
import { ConfigService } from '../config.service';
import { EntryData } from '../entry/structure-data.models';
import { fromFetch } from 'rxjs/fetch';
import { EntryService } from '../entry/entry.service';
import { UtilService } from '../util.service';
import { Router } from '@angular/router';
import { parseLlmAnnotationsResponse } from '../entry/text-annotations-tab/text-annotations.helpers';
@Injectable()
export class EntryEffects {
  private readonly entryAPIService = inject(CommonService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<EntryStoreState>);
  private configService = inject(ConfigService);
  private entryService = inject(EntryService);
  private utilService = inject(UtilService)
  private readonly router = inject(Router);
  
  getEntryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getEntryData),
      switchMap(() => this.store.select(EntrySelectors.accession).pipe(tap())),
      mergeMap((accession: string) => {
        const auth = this.configService.getConfig().apiKey
          ? `?key=${this.configService.getConfig().apiKey}`
          : '';
        return this.entryAPIService
          .getApiData(
            this.configService.getConfig().entryApiUrl + accession + auth,
            { isInternal: true }
          )
          .pipe(
            map((entryData) =>{
              const entryDataArray = entryData[0];
              if(entryData.length > 1 && !accession.includes('AF-')){
                const uniprotAccession = this.utilService.extractUniprotAccession(accession);
                this.router.navigate(
                  ['/search', 'text', accession],
                  // { queryParams: { redirected: true } }
                );
                return;
              }else{

                // return EntryActions.getEntryDataSuccess({ entryData: entryData[0] })
              }
              const macromoleculesData: EntryData[] =
                entryDataArray?.isComplex === true && entryData.length > 1
                  ? entryData
                  : [];
              return EntryActions.getEntryDataSuccess({ entryData: entryDataArray, macromoleculesData })
            }),
            catchError((error) => {
              this.entryService.setShowError(true);
              // this.store.dispatch(EntryActions.getEntryDataSuccess({ entryData: undefined }))
              return of(EntryActions.getEntryDataFailure())
            })
          );
      })
    )
  );

  getUniprotData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getUniprotData),
      switchMap(() => this.store.select(EntrySelectors.uniprotAccession).pipe(tap())),
      mergeMap((uniprotAccession: string) => {
        const accesssionForAPI = uniprotAccession;
        return this.entryAPIService
        .getApiData(this.configService.getConfig().uniprotApiUrl + accesssionForAPI).pipe(
            map((data) =>
              EntryActions.getUniprotDataSuccess({ uniprotData: data })
            ),
            catchError((error) => {
              const failedUniprotData = {
                __status: error?.status === 404 ? 'not_found' : 'error',
                accession: null,
                comments: [],
                sequence: {
                  modified: '',
                },
              };

              return of(EntryActions.getUniprotDataSuccess({ uniprotData: failedUniprotData }));
            })
          );
      })
    )
  );

  getComplexData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getComplexData),
      switchMap(() => this.store.select(EntrySelectors.entryData).pipe(tap())),
      mergeMap((entryData: EntryData) => {
        let accesssionForAPI = entryData.modelEntityId;
        if(!entryData.isComplex){
          accesssionForAPI = entryData.uniprotAccession;
        }
         const auth = this.configService.getConfig().apiKey
          ? `?key=${this.configService.getConfig().apiKey}`
          : '';
        return this.entryAPIService
        .getApiData(this.configService.getConfig().complexUrl + accesssionForAPI + auth, { isInternal: true }).pipe(
            map((data) =>{
              const complexDataArray = Array.isArray(data) ? data : [];
              const summaryComplexData = complexDataArray[0] || {};
              const complexDataList = entryData.isComplex ? (complexDataArray[0] ? [complexDataArray[0]] : []) : complexDataArray;

              return EntryActions.getComplexDataSuccess({
                complexData: summaryComplexData,
                complexDataList,
              });
            }
             
            ),
            catchError((error) => {
            // Handling 404 or other errors:
            // We pass an empty object so the 'filter' in your component sees that the call is 'finished'
            return of(EntryActions.getComplexDataSuccess({ complexData: {}, complexDataList: [] }));
          })
          );
      })
    )
  );

  getCSVData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getCSVData),
      switchMap(() =>
        this.store.select(EntrySelectors.entryData).pipe(take(1))
      ),
      switchMap((entryData: EntryData) => {
        if (entryData?.amAnnotationsUrl) {
          const fileUrl = entryData.amAnnotationsUrl;

          return from(fetch(fileUrl)).pipe(
            switchMap(response => from(response.text())),
            map(csvData =>
              EntryActions.getCSVDataSuccess({ csvAmData: csvData })
            ),
            catchError(error => {
              console.error('Fetch or text() failed', error);
              return of(EntryActions.getCSVDataSuccess({ csvAmData: "" }));
            })
          );
        } else {
          return of(EntryActions.getCSVDataSuccess({ csvAmData: undefined }));
        }
      })
    )
  );

  getDomainData$ = createEffect(() =>
  
    this.actions$.pipe(
      ofType(EntryActions.getTedDomainData),
      switchMap(() => this.store.select(EntrySelectors.accession).pipe(tap())),
      mergeMap((accession: string) => {
        const auth = this.configService.getConfig().apiKey
          ? `?key=${this.configService.getConfig().apiKey}`
          : '';
        let accesssionForAPI = accession
        if(accession.includes('AF-')){
         accesssionForAPI = this.utilService.extractUniprotAccession(accession);
        }
        return this.entryAPIService
          .getApiData(
            this.configService.getConfig().domainsApiUrl + accesssionForAPI + auth,
            { isInternal: true }
          )
          .pipe(
            map((data: any) =>
              EntryActions.getTedDomainDataSuccess({
                tedDomainData: data.annotations,
              })
            ),
            catchError(() => of(EntryActions.getTedDomainDataFailure()))
          );
      })
    )
  );

  getLlmAnnotationsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getLlmAnnotationsData),
      switchMap((action) =>
        // An explicit uniprotAccession (the Text Annotations tab switching to a heterodimer
        // chain with its own accession) is used as-is; otherwise fall back to the entry-level
        // load, which waits on entryData's own uniprotAccession (the representative/first chain).
        action.uniprotAccession
          ? of(action.uniprotAccession)
          : this.store.select(EntrySelectors.entryData).pipe(
              tap(),
              map((entryData: EntryData) => entryData?.uniprotAccession)
            )
      ),
      mergeMap((uniprotAccession: string | undefined) => {
        if (!uniprotAccession) {
          // No UniProt accession to look annotations up by — skip the call entirely rather than
          // hit the endpoint with an empty id. Failure marks the request finished (so the tab chip
          // resolves to "N/A" instead of spinning) and leaves llmAnnotationsData unchanged.
          return of(EntryActions.getLlmAnnotationsDataFailure({}));
        }
        const llmAnnotationsApiUrl = this.configService.getConfig().llmAnnotationsApiUrl + uniprotAccession;
        return this.entryAPIService
          .getApiData(llmAnnotationsApiUrl)
          .pipe(
            map((data: any) =>
              EntryActions.getLlmAnnotationsDataSuccess({
                llmAnnotationsData: parseLlmAnnotationsResponse(data, uniprotAccession),
              })
            ),
            catchError(() => of(EntryActions.getLlmAnnotationsDataFailure({ uniprotAccession })))
          );
      })
    )
  );

  getClustermmseqData = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getClustermmseqData),
      switchMap(() => this.store.select(EntrySelectors.uniprotAccession).pipe(tap())),
      mergeMap((uniprotAccession: string) => {
        
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : "";
    const configUrl = this.configService.getConfig().clusterApiUrl;

    const updatedurl = `${configUrl}${uniprotAccession}?cluster_flag=AFDB50%2FMMseqs2&records=10&start=0&sort_direction=DESC&sort_column=averagePlddt`
    return this.entryAPIService
          .getApiData(
            updatedurl + auth,
            { isInternal: true }
          )
          .pipe(
            map((clustermmseqData) =>
              EntryActions.getClustermmseqSuccess({ clustermmseqData: clustermmseqData })
            ),
            catchError(() => of(EntryActions.getClustermmseqFailure()))
          );
        })
    )
  );

  getClusterfoldseekData = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryActions.getClusterfoldseekData),
      switchMap(() => this.store.select(EntrySelectors.uniprotAccession).pipe(tap())),
      mergeMap((uniprotAccession: string) => {
        
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : "";
    const configUrl = this.configService.getConfig().clusterApiUrl;

    const updatedurl = `${configUrl}${uniprotAccession}?cluster_flag=AFDB%2FFoldseek&records=10&start=0&sort_direction=DESC&sort_column=averagePlddt`
    return this.entryAPIService
          .getApiData(
            updatedurl + auth,
            { isInternal: true }
          )
          .pipe(
            map((clusterfoldseekData) =>
              EntryActions.getClusterfoldseekSuccess({ clusterfoldseekData: clusterfoldseekData })
            ),
            catchError(() => of(EntryActions.getClusterfoldseekFailure()))
          );
        })
    )
  );
}
